import { type AllTaskProperties, PropertiesType } from '../../../types';
import { START_OR_STOP_SHAKE_TASKS } from '../../../utils';
import type { LabwareByHandleIndexByTaskId } from '../../selectors/types';
import type {
  TimeEstimateValuesByTaskId,
  WorkflowSerializationError,
} from '../../shared/types';
import * as TaskErrors from '../taskErrors';

type GetTaskToJsonErrorsProps = {
  graphHasCycles: boolean;
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId;
  taskIds: string[];
  taskPropertiesById: Record<string, AllTaskProperties>;
  timeEstimateValuesByTaskId: TimeEstimateValuesByTaskId;
};

export const getTaskToJsonErrors = ({
  graphHasCycles,
  labwareIdsForTaskByTaskId,
  taskIds,
  taskPropertiesById,
  timeEstimateValuesByTaskId,
}: GetTaskToJsonErrorsProps): WorkflowSerializationError[] => {
  const errors: WorkflowSerializationError[] = [];

  if (graphHasCycles) {
    errors.push({ errorType: 'tasks', message: TaskErrors.CIRCULAR_GRAPH });
  }

  if (Object.keys(labwareIdsForTaskByTaskId).length === 0) {
    errors.push({
      errorType: 'tasks',
      message: TaskErrors.MINIMUM_GRAPH_REQUIREMENTS,
    });
  }

  for (const taskId of taskIds) {
    const task = taskPropertiesById[taskId];
    if (task?.propertiesType && task.propertiesType !== PropertiesType.Action) {
      continue;
    }
    const instrumentId = task?.instrumentId;
    // TODO(IL, 2024-04-09): flatMap as hack for no type-safe filter. Use more semantic util?
    const labwareIdsInTask = Object.values(
      labwareIdsForTaskByTaskId[taskId] ?? []
    ).flatMap((labwareId) => (labwareId === null ? [] : [labwareId]));
    const timeEstimate = timeEstimateValuesByTaskId[taskId];

    if (task === undefined || instrumentId == undefined) {
      if (task === undefined) {
        errors.push({
          errorType: 'tasks',
          message: TaskErrors.NO_TASK_PROPERTIES,
          taskId,
        });
      }

      if (instrumentId == undefined) {
        errors.push({
          errorType: 'tasks',
          message: TaskErrors.MISSING_INSTRUMENT,
          taskId,
        });
      }
    }

    if (labwareIdsInTask.length === 0 && !task?.isLabwareLessTask) {
      errors.push({
        errorType: 'tasks',
        message: TaskErrors.NO_LABWARE_FOR_TASK,
        taskId,
      });
    }

    const taskTypesNotRequiringTimeEstimate = [
      'store',
      'dispose',
      ...START_OR_STOP_SHAKE_TASKS,
    ];

    const taskRequireTimeEstimate = !(
      task && taskTypesNotRequiringTimeEstimate.includes(task.taskType)
    );

    const notValidTimeEstimate =
      !(timeEstimate === undefined) &&
      (timeEstimate === null || timeEstimate <= 0);

    if (taskRequireTimeEstimate && notValidTimeEstimate) {
      errors.push({
        errorType: 'tasks',
        message: TaskErrors.MISSING_TIME_ESTIMATE,
        taskId,
      });
    }
  }
  return errors;
};
